import { useQuery } from '@tanstack/react-query';

import { getClientProducts } from 'api/generated/client-products/client-products';
import type { ClientProductDto } from 'api/generated/schemas';
import { useCurrentUser } from 'hooks/api/use-current-user';

export type ClientProduct = Omit<ClientProductDto, 'type'> & { type: string };

interface UseClientProductsResult {
  clientProducts: ClientProduct[];
  loading: boolean;
  error: unknown;
}

const CLIENT_PRODUCTS_PAGE_SIZE = 1000;

export const clientProductsKeys = {
  all: ['clientProducts'] as const,
  byClient: (userId: string, clientId: string) =>
    [...clientProductsKeys.all, userId, clientId] as const,
};

const useClientProducts = (): UseClientProductsResult => {
  const currentUser = useCurrentUser();
  const userId = currentUser.data?.oktaUserId;
  const clientId = currentUser.data?.client?.id;

  const productsQuery = useQuery({
    queryKey: clientProductsKeys.byClient(userId ?? '', String(clientId ?? '')),
    queryFn: () =>
      getClientProducts(userId as string, Number(clientId), {
        size: CLIENT_PRODUCTS_PAGE_SIZE,
      }),
    enabled: !!userId && !!clientId,
    select: (list) =>
      (list ?? []).map((product) => ({
        ...product,
        type: String(product.type ?? ''),
      })),
  });

  return {
    clientProducts: productsQuery.data ?? [],
    loading: productsQuery.isLoading,
    error: productsQuery.error,
  };
};

export default useClientProducts;
